import config from '../../config'
import React, { useState, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useLocation, useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import { Light as CodeBlock } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import useTheme from '@material-ui/styles/useTheme'
import Skeleton from '@material-ui/lab/Skeleton'
import AlarmTrackerIcon from '@material-ui/icons/TrackChanges'

// contexts
import { useClient } from '../../contexts/ClientContext'

// styles
import { github as hljsStyle } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import useStyles from './styles'

// utilities
import { getStatusTextSafe, guardedRequest } from '../../utils'

// components
import PageTitle from '../../components/PageTitle/PageTitle'
import Widget from '../../components/Widget/Widget'
import { Typography } from '../../components/Wrappers'

CodeBlock.registerLanguage('json', json)

export default function LogDetails (props) {
  const classes = useStyles()
  const theme = useTheme()
  const { client, setFilter, setFilteredLogs } = useClient()

  const [cookies] = useCookies(['accessToken'])
  const history = useHistory()
  const location = useLocation()
  const params = useParams()

  const [pageError, setPageError] = useState(false)
  const [pageProcessing, setPageProcessing] = useState(false)
  const [log, setLog] = useState({})

  const requestId = params.id || props.requestId

  useEffect(() => {
    let isCancelled = false

    async function runEffect () {
      setPageProcessing(true)
      try {
        if (client) {
          const clientId = client.client_id
          if (!clientId) { return history.push('/app/debugger') }
          const newLog = await guardedRequest(location, {
            url: `${config.apiUrl}/clients/${clientId}/logs/${requestId}`,
            auth: { bearer: cookies.accessToken },
            method: 'get',
            json: true
          })
          if (isCancelled) { return }
          setLog(newLog)
        }
      } catch (error) {
        if (error.message !== 'Unauthorized') setPageError(true)
        console.log(error)
      }
      setPageProcessing(false)
    }

    function cancelEffect () {
      isCancelled = true
    }

    runEffect()
    return cancelEffect
  }, [client, cookies.accessToken, history, location, requestId])

  return (
    <Grid container spacing={4}>
      {!props.requestId && <Hidden lgDown><Grid item xl={1}></Grid></Hidden>}
      <Grid item xs={12} xl={props.requestId ? 12 : 10}>
        {pageError && <PageTitle title='Something went wrong!' />}

        {pageError &&
        <Typography variant='body1'>
          We were unable to fetch the details for this logged request. Please reload the page to retry.
        </Typography>
        }

        {!pageError && client && log &&
        <>
        <PageTitle
          title="Log Details"
          button={
            history.location.pathname.toLowerCase() !== '/app/alarmtracker' &&
            log && log.request && log.request.route &&
            /^\/dispatch\/.*$/.test(log.request.route) &&
            ((/^.*alarms$/.test(log.request.route) && log.response && log.response.status === 201) ||
            !(/^.*alarms$/.test(log.request.route))) &&
          {
            title: 'Track Alarm',
            disabled: pageProcessing,
            action: () => {
              try {
                const alarmId = (/^.*alarms$/.test(log.request.route)
                  ? log.response && log.response.status === 201 && log.response.body && log.response.body.id
                  : log.request.route.match(/alarms\/(?<id>\w*)/).groups.id)
                if (alarmId) {
                  setFilter(filter => Object.assign({}, filter, { alarmId }))
                  setFilteredLogs()
                  history.push('/app/alarmtracker')
                }
              } catch (error) {
                console.log(error)
              }
            },
            icon: AlarmTrackerIcon
          }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Widget disableWidgetMenu>
              {log.request &&
              <Typography variant='h4' display='inline'>
                {`${log.request.verb} ${log.request.route}`}
              </Typography>}
              {!log.request && <Skeleton width='100%' height={60} />}
            </Widget>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Widget disableWidgetMenu title='Summary'>
              <TableContainer>
                <Table>
                  <TableBody>
                    {!pageProcessing && log.request && <>
                    <TableRow>
                      <TableCell>
                        <Typography variant='subtitle2'>Time</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{moment(log.request.time).format('M/D/YYYY, h:mm:ss a')}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant='subtitle2'>Method</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{log.request.verb}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant='subtitle2'>URL</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{log.request.route}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant='subtitle2'>Response Status</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body2'
                          color={log.response.status >= 400 ? 'error' : 'success'}
                        >
                          {`${log.response.status} ${getStatusTextSafe(log.response.status)}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    </>}
                    <>
                    {!!pageProcessing && !log.request && '....'.split('').map((e, i) =>
                      <TableRow key={i}>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                      </TableRow>
                    )}
                    </>
                  </TableBody>
                </Table>
              </TableContainer>
            </Widget>
          </Grid>
        </Grid>
        {log.request &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Widget disableWidgetMenu title='Request'>
              {log.request.headers && <>
              <Typography variant="subtitle1" className={classes.codeBlockHeader}>Headers</Typography>
              <CodeBlock
                language='json'
                style={hljsStyle}
                customStyle={{ fontSize: theme.typography.fontSize, marginTop: '5px' }}
              >
                {JSON.stringify(log.request.headers, null, 2)}
              </CodeBlock>
              </>}
              {log.request.body && <>
              <Typography variant="subtitle1" className={classes.codeBlockHeader}>Body</Typography>
              <CodeBlock
                language={typeof log.request.body === 'object' ? 'json' : 'text'}
                style={hljsStyle}
                customStyle={{ fontSize: theme.typography.fontSize, marginTop: '5px' }}
              >
                { typeof log.request.body === 'object'
                  ? JSON.stringify(log.request.body, null, 2)
                  : log.request.body
                }
              </CodeBlock>
              </>}
            </Widget>
          </Grid>
        </Grid>}
        {log.response &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Widget disableWidgetMenu title='Response'>
              {log.response.headers && <>
              <Typography variant="subtitle1" className={classes.codeBlockHeader}>Headers</Typography>
              <CodeBlock
                language='json'
                style={hljsStyle}
                customStyle={{ fontSize: theme.typography.fontSize, marginTop: '5px' }}
              >
                {JSON.stringify(log.response.headers, null, 2)}
              </CodeBlock>
              </>}
              {log.response.body && <>
              <Typography variant="subtitle1" className={classes.codeBlockHeader}>Body</Typography>
              <CodeBlock
                language={typeof log.response.body === 'object' ? 'json' : 'text'}
                style={hljsStyle}
                customStyle={{ fontSize: theme.typography.fontSize, marginTop: '5px' }}
              >
                { typeof log.response.body === 'object'
                  ? JSON.stringify(log.response.body, null, 2)
                  : log.response.body
                }
              </CodeBlock>
              </>}
            </Widget>
          </Grid>
        </Grid>}
        </>
        }
      </Grid>
    </Grid>
  )
}
